import React from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useQuery } from "@tanstack/react-query";
import { getClient } from "../data";
import SocialMediaIcons from "./social-media-icons";
import {
  Mail,
  Phone,
  Building,
  CreditCard,
  Globe,
  User,
  Hash,
  FileText,
  MapPin,
  Briefcase,
  Landmark,
  Banknote,
} from "lucide-react";

export function ClientViewDialog({ isOpen, onClose, clientId }) {
  const { data, isLoading, error } = useQuery({
    queryKey: ["client", clientId],
    queryFn: () => getClient(clientId),
    enabled: !!clientId,
  });

  if (isLoading) return null; // Dialog loading state handled by parent or overlay
  if (error) return null;
  if (!data) return null;

  const client = data.data;

  const InfoItem = ({ icon: Icon, label, value }) => (
    <div className="flex items-start gap-3 p-2 rounded-md hover:bg-muted transition-colors">
      <div className="mt-0.5 p-1.5 bg-blue-100 dark:bg-blue-950/50 rounded-md text-blue-600 dark:text-blue-400">
        <Icon className="h-4 w-4" />
      </div>
      <div>
        <p className="text-xs font-medium text-muted-foreground">{label}</p>
        <p className="text-sm font-medium text-foreground break-all">{value || "-"}</p>
      </div>
    </div>
  );

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto">
        <DialogHeader className="pb-4 border-b">
          <div className="flex items-center gap-3">
            <div className="h-12 w-12 rounded-full bg-blue-600 flex items-center justify-center text-white text-xl font-bold">
              {client.name.charAt(0).toUpperCase()}
            </div>
            <div>
              <DialogTitle className="text-xl">{client.name}</DialogTitle>
              <p className="text-sm text-muted-foreground flex items-center gap-1 mt-1">
                <Hash className="h-3 w-3" />
                ID: {client.id}
              </p>
            </div>
          </div>
        </DialogHeader>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 py-4">
          {/* Contact Information */}
          <div className="space-y-3">
            <h4 className="text-sm font-semibold text-foreground flex items-center gap-2">
              <User className="h-4 w-4" /> Contact Information
            </h4>
            <div className="space-y-1">
              <InfoItem icon={Mail} label="Email" value={client.email} />
              <InfoItem icon={Phone} label="Phone" value={client.phoneNumber} />
              <InfoItem icon={MapPin} label="Country" value={client.country} />
              <InfoItem icon={Briefcase} label="Account Manager" value={client.newTvAccountManager} />
            </div>
          </div>

          {/* Financial Information */}
          <div className="space-y-3">
            <h4 className="text-sm font-semibold text-foreground flex items-center gap-2">
              <Landmark className="h-4 w-4" /> Financial Details
            </h4>
            <div className="space-y-1">
              <InfoItem icon={Building} label="Billing Name" value={client.billingName} />
              <InfoItem icon={Banknote} label="Bank Name" value={client.bankName} />
              <InfoItem icon={CreditCard} label="IBAN" value={client.IBAN} />
              <InfoItem icon={Globe} label="SWIFT" value={client.SWIFT} />
              <InfoItem icon={Banknote} label="Currency" value={client.currency} />
            </div>
          </div>

          {/* System Information */}
          <div className="space-y-3">
            <h4 className="text-sm font-semibold text-foreground flex items-center gap-2">
              <FileText className="h-4 w-4" /> System Integration
            </h4>
            <div className="space-y-1">
              <InfoItem icon={Hash} label="Luca ID" value={client.lucaId} />
              <InfoItem icon={User} label="Luca Account Name" value={client.lucaAccountName} />
            </div>
          </div>

          {/* Social Media */}
          <div className="space-y-3">
            <h4 className="text-sm font-semibold text-foreground flex items-center gap-2">
              <Globe className="h-4 w-4" /> Social Media
            </h4>
            <div className="p-2">
              <SocialMediaIcons data={client.socialMediaAccounts} />
            </div>
          </div>
        </div>

        {/* Custom Data */}
        {client.customData && client.customData.length > 0 && (
          <div className="mt-2 pt-4 border-t">
            <h4 className="text-sm font-semibold text-foreground mb-3">Custom Data</h4>
            <div className="grid grid-cols-2 gap-4">
              {client.customData.map((item, index) => (
                <div key={index} className="bg-muted p-2 rounded text-sm">
                  <span className="font-medium text-muted-foreground">{item.key}:</span>{" "}
                  <span className="text-foreground">{item.value}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="flex justify-end pt-4 border-t mt-4">
          <Button onClick={onClose} variant="outline">Close</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
